'use client';

import { useState, useEffect } from 'react';
import BlobImage from '@/components/BlobImage';
import { Card } from '@/components/ui/Card';
import { Chip } from '@/components/ui/Chip';
import { ItalicBody } from '@/components/ui/ItalicBody';
import { MonoLabel } from '@/components/ui/MonoLabel';
import { useI18n } from '@/lib/i18n';
import { compressImage } from '@/lib/imageCompress';
import type { Yarn } from '@/lib/types';
import { bucketForHex, BUCKET_SWATCHES, bucketRepresentativeHex, type ColorBucket } from '@/lib/colorBucket';
import { classifyYarnWeight, type YarnWeightClass } from '@/lib/weightClassify';
import { useMeasurementSystem } from '@/lib/units';

/** Standard knitting needle sizes in mm — covers the European range
 *  from lace (1.5) through super-bulky/jumbo (25). The dropdown uses
 *  this list so labels with comma decimals or non-standard formatting
 *  can't break the auto-classifier (Veronika 2026-05-05). */
const NEEDLE_SIZES = [
  1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 3.25, 3.5, 3.75, 4, 4.5,
  5, 5.5, 6, 6.5, 7, 8, 9, 10, 12, 15, 20, 25,
];

/** Pull a low/high needle pair out of an existing free-text string,
 *  snapping each to the nearest standard size. Used to migrate
 *  pre-1035 free-text data into the new From/To pickers without
 *  asking the user to re-enter every yarn. */
function parseInitialNeedles(s: string | undefined): { from: number | null; to: number | null } {
  if (!s) return { from: null, to: null };
  const normalized = s.replace(/,/g, '.');
  const numbers = normalized.match(/\d+(?:\.\d+)?/g);
  if (!numbers) return { from: null, to: null };
  const nums = numbers.map(Number).filter(n => isFinite(n) && n > 0 && n < 30);
  if (nums.length === 0) return { from: null, to: null };
  const snap = (n: number) => NEEDLE_SIZES.reduce((best, cur) =>
    Math.abs(cur - n) < Math.abs(best - n) ? cur : best);
  if (nums.length === 1) return { from: null, to: snap(nums[0]) };
  return { from: snap(Math.min(...nums)), to: snap(Math.max(...nums)) };
}

function formatNeedleRange(from: number | null, to: number | null): string {
  if (from != null && to != null) {
    if (from === to) return `${from} mm`;
    return `${from} mm - ${to} mm`;
  }
  if (to != null) return `${to} mm`;
  if (from != null) return `${from} mm`;
  return '';
}

const DEFAULT_BRANDS = [
  'Knitting for Olive', 'Sandnes Garn', 'Isager', 'Filcolana', 'CaMaRose', 'BC Garn',
  'Lang Yarns', 'Cardiff Cashmere', 'Lamana', 'Holst Garn', 'Wool Addicts',
  'Drops / Garnstudio', 'Rauma', 'Dale of Norway', 'Viking of Norway', 'Novita',
  'Hjertegarn', 'Mohair by Canard', 'Arne & Carlos',
  'Schachenmayr', 'Lana Grossa', 'Schoppel-Wolle', 'Rico Design', 'Regia', 'Opal',
  'Pascuali', 'Kremke Soul Wool', 'Atelier Zitron', 'Gedifra', 'Gabo Wool',
  'Rowan', 'West Yorkshire Spinners', 'Erika Knight', 'Debbie Bliss', 'John Arbon', 'Jamieson & Smith',
  'Foa', 'Adriafil', 'Mondial', 'Filatura di Crosa', 'Laines du Nord',
  'Katia', 'Valeria di Roma', 'Rosarios 4', 'Retrosaria Rosa Pomar',
  'Bergere de France', 'Phildar', 'De Rerum Natura', 'La Droguerie', 'Fonty',
  'Scheepjes', 'Durable',
  'Majo Garn', 'Pru Yarns', 'By Permin', 'Amano',
  'Alize', 'YarnArt', 'Nako', 'Himalaya',
  'Malabrigo', 'Cascade', 'Berroco', 'Brooklyn Tweed', 'Quince & Co', 'Manos del Uruguay',
].sort();

export default function YarnForm({ initial, onSubmit, onCancel }: {
  initial?: Yarn;
  onSubmit: (data: Omit<Yarn, 'id' | 'createdAt'>) => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const measurementSystem = useMeasurementSystem();
  const [name, setName] = useState(initial?.name || '');
  const [brand, setBrand] = useState(initial?.brand || '');
  const [isCustomBrand, setIsCustomBrand] = useState(initial?.brand ? !DEFAULT_BRANDS.includes(initial.brand) : false);
  const [fiber, setFiber] = useState(initial?.fiber || '');
  const [weight, setWeight] = useState(initial?.weight || "I don't know");
  // True once the user has explicitly picked a weight from the dropdown.
  // Until then we treat the field as auto-managed by classifyYarnWeight
  // and refresh it whenever grams / meters / needles change. After
  // override the auto-suggester goes silent for the rest of this form.
  // Pre-existing yarns boot in "manual" mode so their stored weight
  // doesn't suddenly mutate when the form opens.
  const [weightManual, setWeightManual] = useState<boolean>(!!initial?.weight && initial.weight !== "I don't know");
  const [colorName, setColorName] = useState(initial?.colorName || '');
  const [colorNumber, setColorNumber] = useState(initial?.colorNumber || '');
  // Build 1040 (Veronika 2026-05-05): colorBucket replaces the
  // free-form colorHex picker AND the separate "Multicolor" checkbox.
  // Derive an initial bucket for legacy yarns so the dropdown shows
  // a sensible default on first edit.
  const initialBucket: ColorBucket | 'multicolor' | null = (() => {
    if (initial?.colorBucket) return initial.colorBucket;
    if (initial?.isMulticolor) return 'multicolor';
    if (initial?.colorHex) {
      const b = bucketForHex(initial.colorHex);
      if (b !== 'unknown') return b;
    }
    return null;
  })();
  const [colorBucket, setColorBucket] = useState<ColorBucket | 'multicolor' | null>(initialBucket);
  // colorHex is now auto-derived from the chosen bucket (so the rest
  // of the UI keeps reading the same field for swatch fallback). We
  // still preserve any pre-existing user-picked hex when no bucket is
  // selected.
  const colorHex = colorBucket
    ? bucketRepresentativeHex(colorBucket)
    : (initial?.colorHex || '#D4A574');
  const [colorPhoto, setColorPhoto] = useState<string | undefined>(initial?.colorPhoto);

  const handleColorPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    input.value = ''; // clear so picking the SAME file again still fires onChange
    if (!file) return;
    const compressed = await compressImage(file, { maxWidth: 400, maxHeight: 400, quality: 0.82 });
    setColorPhoto(compressed);
  };
  // `yardage` is always stored in meters in Dexie; UI displays whichever
  // unit matches the user's preference. `displayedYardage` is the
  // number the input shows; yardage itself tracks meters.
  const [yardage, setYardage] = useState(initial?.yardage || 0);
  const displayedYardage = measurementSystem === 'imperial'
    ? (yardage ? Math.round(yardage * 1.0936133) : 0)
    : yardage;
  const setDisplayedYardage = (value: number) => {
    if (!isFinite(value) || value <= 0) { setYardage(0); return; }
    const meters = measurementSystem === 'imperial'
      ? Math.round(value * 0.9144)
      : value;
    setYardage(meters);
  };
  const lengthPerSkeinLabel = measurementSystem === 'imperial'
    ? t.metersPerSkein.replace(/Meters|Metry/i, match => match === 'Metry' ? 'Yardy' : 'Yards')
    : t.metersPerSkein;
  const [gramsPerSkein, setGramsPerSkein] = useState(initial?.gramsPerSkein || 0);
  const [pricePerSkein, setPricePerSkein] = useState(initial?.pricePerSkein || 0);
  const [priceCurrency, setPriceCurrency] = useState(initial?.priceCurrency || 'Kč');
  const [skeinCount, setSkeinCount] = useState(initial?.skeinCount ?? 1);
  const [partialSkein, setPartialSkein] = useState<number | undefined>(initial?.partialSkein);
  const [partialGrams, setPartialGrams] = useState<number | undefined>(initial?.partialGrams);
  const [review, setReview] = useState(initial?.review || '');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [shopUrl, setShopUrl] = useState(initial?.shopUrl || '');
  const initialNeedles = parseInitialNeedles(initial?.recommendedNeedles);
  const [needlesFrom, setNeedlesFrom] = useState<number | null>(initialNeedles.from);
  const [needlesTo, setNeedlesTo] = useState<number | null>(initialNeedles.to);
  const recommendedNeedles = formatNeedleRange(needlesFrom, needlesTo);
  const [mfgGaugeStitches, setMfgGaugeStitches] = useState(initial?.mfgGaugeStitches || 0);
  const [mfgGaugeStitchesMax, setMfgGaugeStitchesMax] = useState(initial?.mfgGaugeStitchesMax || 0);
  const [mfgGaugeRows, setMfgGaugeRows] = useState(initial?.mfgGaugeRows || 0);
  const [mfgGaugeNeedle, setMfgGaugeNeedle] = useState(initial?.mfgGaugeNeedle || '');
  const [myGaugeStitches, setMyGaugeStitches] = useState(initial?.myGaugeStitches || 0);
  const [myGaugeRows, setMyGaugeRows] = useState(initial?.myGaugeRows || 0);
  const [myGaugeNeedle, setMyGaugeNeedle] = useState(initial?.myGaugeNeedle || '');
  const [photo, setPhoto] = useState<string | undefined>(initial?.photo);

  // Recompute the auto-suggested weight whenever its inputs change.
  // Gauge stitches per 10cm is the primary signal — it accounts for
  // halo/loft on tweed/mohair/brushed yarns where m/100g misleads.
  const suggestedWeight: YarnWeightClass | null = classifyYarnWeight({
    metersPerSkein: yardage,
    gramsPerSkein,
    gaugeStitches: mfgGaugeStitches,
    gaugeStitchesMax: mfgGaugeStitchesMax,
    recommendedNeedles,
  });

  // Push the suggestion into the weight field as long as the user
  // hasn't manually overridden. Effect (rather than a render-time
  // setState) so we don't loop and so the value is observable to the
  // submit handler. Not gated on `weight !== suggestedWeight` so the
  // field reverts to "I don't know" when the user clears the inputs.
  useEffect(() => {
    if (weightManual) return;
    setWeight(suggestedWeight ?? "I don't know");
  }, [suggestedWeight, weightManual]);

  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    input.value = ''; // clear so picking the SAME file again still fires onChange
    if (!file) return;
    const compressed = await compressImage(file);
    setPhoto(compressed);
  };

  const handleSubmit = (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (!name.trim()) return;

    // 2026-05-19 build 1470 (Veronika msg 4161-4169): when the user adds
    // gramsPerSkein to a yarn that previously had none but DID have a
    // fractional partialSkein recorded (e.g. Duhovka: 1 skein + ¼,
    // gramsPerSkein null → now 30), that ¼ partial silently joins the
    // stock math as 7.5g. She finished a project shortly after and saw
    // the leftover land at an unexpected 24.5g (instead of her expected
    // 17g) because the ¼ partial was invisible before the gramsPerSkein
    // came online. One-time confirm to keep or clear the partial gives
    // her a chance to audit before it joins the math.
    let nextPartialSkein = partialSkein || 0;
    const wasGramsUnset = !(initial?.gramsPerSkein && initial.gramsPerSkein > 0);
    const isGramsNowSet = !!(gramsPerSkein && gramsPerSkein > 0);
    if (wasGramsUnset && isGramsNowSet && nextPartialSkein > 0) {
      const fractionGlyph =
        nextPartialSkein === 0.25 ? '¼' :
        nextPartialSkein === 0.5 ? '½' :
        nextPartialSkein === 0.75 ? '¾' :
        String(nextPartialSkein);
      const partialGramsImplied = Math.round(nextPartialSkein * gramsPerSkein * 10) / 10;
      const wholeLabel = `${skeinCount} ${skeinCount === 1 ? t.skein.toLowerCase() : t.skeins.toLowerCase()}`;
      const msg = t.yarnPartialOnGramsAddPrompt
        .replace('{fraction}', fractionGlyph)
        .replace('{name}', name)
        .replace(/\{grams\}/g, String(gramsPerSkein))
        .replace(/\{partialGrams\}/g, String(partialGramsImplied))
        .replace(/\{wholeLabel\}/g, wholeLabel);
      const keep = confirm(msg);
      if (!keep) nextPartialSkein = 0;
    }

    onSubmit({
      name, brand, fiber, weight, colorName, colorNumber, colorHex,
      colorPhoto: colorPhoto || undefined,
      colorBucket: colorBucket && colorBucket !== 'unknown' ? colorBucket : undefined,
      // Preserve legacy isMulticolor flag in storage for sync compat
      // — read-only once the new picker is live.
      isMulticolor: initial?.isMulticolor,
      yardage, gramsPerSkein: gramsPerSkein || undefined,
      pricePerSkein: pricePerSkein || undefined,
      priceCurrency: pricePerSkein ? priceCurrency : undefined,
      skeinCount,
      // Explicit 0 instead of `undefined` when cleared: `Dexie.update`
      // silently ignores keys whose value is undefined, so passing
      // `undefined` to clear the other partial-mode field leaves the
      // stale value in the row and in the CRDT — Veronika's
      // 2026-04-23 "¼ + 23g" merge bug. 0 is a concrete write that
      // Dexie + Automerge both propagate, and the display code
      // already treats 0 as falsy ("no partial").
      partialSkein: nextPartialSkein,
      partialGrams: partialGrams || 0,
      review: review || undefined,
      notes: notes || undefined,
      shopUrl: shopUrl || undefined,
      recommendedNeedles: recommendedNeedles || undefined,
      mfgGaugeStitches: mfgGaugeStitches || undefined,
      mfgGaugeStitchesMax: mfgGaugeStitchesMax || undefined,
      mfgGaugeRows: mfgGaugeRows || undefined,
      mfgGaugeNeedle: mfgGaugeNeedle || undefined,
      myGaugeStitches: myGaugeStitches || undefined,
      myGaugeRows: myGaugeRows || undefined,
      myGaugeNeedle: myGaugeNeedle || undefined,
      photo,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
      {/* Photo — 2026-05-10 build 1270, B1 zine: ink-bordered
          cream square + dashed-ink CHOOSE/CHANGE pill (same
          pattern as the InventoryForm photo picker). */}
      <div>
        <label
          style={{
            display: 'block',
            fontFamily: 'var(--font-mono)',
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--ink-2)',
            marginBottom: 6,
          }}
        >
          {t.photo}
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {photo ? (
            <BlobImage src={photo} style={{ width: 64, height: 64, objectFit: 'cover', border: '1px solid var(--ink)' }} />
          ) : (
            <Card tone="paper-1" style={{ width: 64, height: 64 }}>{null}</Card>
          )}
          <label
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--ink)',
              background: 'var(--paper-2)',
              border: '1px dashed var(--ink)',
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            {photo ? t.change : t.choose}
            <input type="file" accept="image/*" onChange={handlePhoto} style={{ display: 'none' }} />
          </label>
        </div>
      </div>

      <Field label={t.yarnName} value={name} onChange={setName} required placeholder="e.g. Malabrigo Rios" />
      {/* Brand — dropdown with built-in list */}
      <div>
        <MonoLabel>{t.brand}</MonoLabel>
        {isCustomBrand ? (
          <div className="flex gap-2">
            <input type="text" value={brand} onChange={e => setBrand(e.target.value)} placeholder={t.typeBrandName} className="flex-1 px-3.5 py-2.5 bg-[#F5EFDC] border border-ink rounded-none text-sm" autoFocus />
            <button type="button" onClick={() => { setIsCustomBrand(false); setBrand(''); }} className="px-3 py-2 text-[10px] font-medium rounded-xl">{t.list}</button>
          </div>
        ) : (
          <select value={brand} onChange={e => { if (e.target.value === '__new__') { setIsCustomBrand(true); setBrand(''); } else { setBrand(e.target.value); } }} className="w-full px-3.5 py-2.5 bg-[#F5EFDC] border border-ink rounded-none text-sm text-text-primary">
            <option value="">{t.selectBrand}</option>
            <option value="__new__">{t.addNewBrand}</option>
            {DEFAULT_BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        )}
      </div>
      <Field label={t.fiberContent} value={fiber} onChange={setFiber} placeholder="e.g. 100% Merino" />

      <div className="flex gap-3">
        <div className="flex-1">
          <Field label={t.colorName} value={colorName} onChange={setColorName} placeholder="e.g. Dusty Rose" />
        </div>
        <div className="w-20">
          <Field label={t.colorNumber} value={colorNumber} onChange={setColorNumber} placeholder="e.g. 432" />
        </div>
      </div>

      {/* Color bucket — 10-option palette picker (Veronika 2026-05-05).
          Used for By-Color grouping and as the swatch fallback when no
          colorPhoto is set. */}
      <div>
        <MonoLabel>{t.color}</MonoLabel>
        <div className="flex gap-2 items-center">
          <span
            // 2026-05-10 build 1302 — Veronika reversed: "make
            // these too round (bc later we are viewing them round
            // at the card, so it will visually correspond)." The
            // YarnSwatch on cards renders circular, so the form
            // preview should match.
            className="w-10 h-10 rounded-full border border-ink flex-shrink-0"
            style={{
              background: colorBucket === 'multicolor'
                ? 'conic-gradient(#FB5E00, #FCC117, #3FA34D, #2D7FF9, #8E44AD, #E40000, #FB5E00)'
                : colorHex,
            }}
            aria-hidden
          />
          <select
            value={colorBucket || ''}
            onChange={e => setColorBucket((e.target.value || null) as ColorBucket | 'multicolor' | null)}
            className="flex-1 px-3.5 py-2.5 bg-[#F5EFDC] border border-ink rounded-none text-sm text-text-primary"
          >
            <option value="">{t.selectColorBucket}</option>
            {BUCKET_SWATCHES.map(b => (
              <option key={b.bucket} value={b.bucket}>{t[b.labelKey]}</option>
            ))}
            <option value="multicolor">{t.colorBucketMulticolor}</option>
          </select>
        </div>
        <ItalicBody style={{ marginTop: 6 }}>{t.colorBucketHint}</ItalicBody>
      </div>

      {/* Detail photo of the yarn color — overrides the flat
          swatch everywhere. 2026-05-10 build 1301 — Veronika:
          "Here also needs to be space for foto." The empty photo
          placeholder was a barely-visible dashed-border-light
          square; bumped to a clear ink-bordered cream tile so
          the "where the photo will go" affordance reads at a
          glance. Take/upload link converted to the canonical
          dashed-ink mono caps "+ TAKE / UPLOAD" pill (same
          recipe as the AddYarnModal first-photo + InventoryForm
          pickers). */}
      <div>
        <MonoLabel>{t.colorPhotoLabel}</MonoLabel>
        <div className="flex items-start gap-3">
          {colorPhoto ? (
            <BlobImage src={colorPhoto} alt={colorName} style={{ width: 64, height: 64, objectFit: 'cover', border: '1px solid var(--ink)', flexShrink: 0, borderRadius: '50%' }} />
          ) : (
            <Card tone="paper-1" style={{ width: 64, height: 64, flexShrink: 0, borderRadius: '50%' }}>{null}</Card>
          )}
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <label
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--ink)',
                background: 'var(--paper-2)',
                border: '1px dashed var(--ink)',
                padding: '8px 12px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                alignSelf: 'flex-start',
              }}
            >
              {t.takeColorPhoto}
              <input type="file" accept="image/*" onChange={handleColorPhoto} className="hidden" />
            </label>
            {colorPhoto && (
              <button
                type="button"
                onClick={() => setColorPhoto(undefined)}
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: 'var(--over-num)',
                  background: 'transparent',
                  border: 'none',
                  padding: '4px 0',
                  cursor: 'pointer',
                  alignSelf: 'flex-start',
                }}
              >
                {t.removeColorPhoto}
              </button>
            )}
            <ItalicBody>{t.colorPhotoHint}</ItalicBody>
          </div>
        </div>
      </div>

      {/* 2026-05-10 build 1298 — Veronika: SKEINS IN STASH on its
          own next line (the label was wrapping in the narrow third
          column of the 3-col grid). Now METERS / GRAMS share a
          2-col row and SKEINS IN STASH gets a full-width row below
          with the stepper. */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <MonoLabel>{lengthPerSkeinLabel}</MonoLabel>
          <input type="number" value={displayedYardage || ''} onChange={e => setDisplayedYardage(parseInt(e.target.value) || 0)} placeholder={measurementSystem === 'imperial' ? 'e.g. 220' : 'e.g. 200'} className="w-full px-3 py-2.5 bg-[#F5EFDC] border border-ink rounded-none text-sm" />
        </div>
        <div>
          <MonoLabel>{t.gramsPerSkein}</MonoLabel>
          <input type="number" value={gramsPerSkein || ''} onChange={e => setGramsPerSkein(parseInt(e.target.value) || 0)} placeholder="e.g. 50" className="w-full px-3 py-2.5 bg-[#F5EFDC] border border-ink rounded-none text-sm" />
        </div>
      </div>
      <div>
        <MonoLabel>{t.skeinsInStash}</MonoLabel>
        <div className="flex items-stretch bg-[#F5EFDC] border border-ink rounded-none overflow-hidden" style={{ width: 140 }}>
          <button
            type="button"
            onClick={() => setSkeinCount(Math.max(0, skeinCount - 1))}
            aria-label="Decrease skein count"
            className="w-9 flex items-center justify-center text-base text-text-secondary active:bg-bg-card disabled:opacity-30"
            disabled={skeinCount <= 0}
          >
            −
          </button>
          <input
            type="number"
            value={skeinCount}
            onChange={e => setSkeinCount(parseInt(e.target.value) || 0)}
            min={0}
            className="flex-1 min-w-0 text-center bg-transparent text-sm py-2.5 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <button
            type="button"
            onClick={() => setSkeinCount(skeinCount + 1)}
            aria-label="Increase skein count"
            className="w-9 flex items-center justify-center text-base text-text-secondary active:bg-bg-card"
          >
            +
          </button>
        </div>
      </div>

      {/* Partial skein picker — either pick a fraction (¼/½/¾) OR
          type exact grams. The two modes are mutually exclusive and
          whichever the user entered is what the app stores/displays
          (no ratio conversion).
          2026-05-10 build 1294 — Veronika asked for this whole row
          on one line. Pulled OUT of the narrow SKEINS IN STASH
          column (where it wrapped to 3-4 lines on a phone) so it
          spans the modal's full width. */}
      <div className="flex items-center gap-2 flex-wrap">
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-2)' }}>{t.partialSkein}</span>
        {([0.25, 0.5, 0.75] as const).map(val => {
          const active = partialSkein === val;
          const label = val === 0.25 ? '¼' : val === 0.5 ? '½' : '¾';
          return (
            <button
              key={val}
              type="button"
              onClick={() => {
                setPartialSkein(active ? undefined : val);
                setPartialGrams(undefined);
              }}
              className={`w-7 h-7 rounded-none text-sm font-medium transition-colors ${
                active
                  ? 'bg-ink text-[#EDE6D2] border border-ink'
                  : 'bg-[#F5EFDC] text-ink border border-ink hover:bg-[#EDE6D2]'
              }`}
            >
              {label}
            </button>
          );
        })}
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-2)' }}>{t.or}</span>
        <div className={`h-7 flex items-center bg-[#F5EFDC] rounded-none overflow-hidden ${partialGrams ? 'border-2 border-ink' : 'border border-ink'}`}>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            value={partialGrams ?? ''}
            onChange={e => {
              const n = parseInt(e.target.value);
              if (Number.isFinite(n) && n > 0) {
                setPartialGrams(n);
                setPartialSkein(undefined);
              } else {
                setPartialGrams(undefined);
              }
            }}
            placeholder="0"
            aria-label={t.exactGrams}
            className="w-9 h-full text-center bg-transparent text-sm py-0 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="text-[10px] text-text-tertiary pr-1.5">g</span>
        </div>
      </div>

      {/* Price per skein */}
      <div>
        <MonoLabel>{t.pricePerSkein}</MonoLabel>
        <div className="flex gap-2">
          <input
            type="number"
            step="0.01"
            value={pricePerSkein || ''}
            onChange={e => setPricePerSkein(parseFloat(e.target.value) || 0)}
            placeholder="e.g. 180"
            className="flex-1 px-3.5 py-2.5 bg-[#F5EFDC] border border-ink rounded-none text-sm"
          />
          <select
            value={priceCurrency}
            onChange={e => setPriceCurrency(e.target.value)}
            className="w-20 px-2 py-2.5 bg-[#F5EFDC] border border-ink rounded-none text-sm text-text-primary"
          >
            <option value="Kč">Kč</option>
            <option value="€">€</option>
            <option value="$">$</option>
            <option value="£">£</option>
          </select>
        </div>
        <ItalicBody style={{ marginTop: 4 }}>{t.pricePerSkeinHint}</ItalicBody>
      </div>

      <div>
        <MonoLabel>{t.recommendedNeedles}</MonoLabel>
        <div className="grid grid-cols-2 gap-2">
          <select
            value={needlesFrom == null ? '' : String(needlesFrom)}
            onChange={e => setNeedlesFrom(e.target.value === '' ? null : Number(e.target.value))}
            className="w-full px-3.5 py-2.5 bg-[#F5EFDC] border border-ink rounded-none text-sm text-text-primary"
            aria-label={t.needleSizeFrom}
          >
            <option value="">{t.needleSizeFrom}</option>
            {NEEDLE_SIZES.map(s => <option key={s} value={s}>{s} mm</option>)}
          </select>
          <select
            value={needlesTo == null ? '' : String(needlesTo)}
            onChange={e => setNeedlesTo(e.target.value === '' ? null : Number(e.target.value))}
            className="w-full px-3.5 py-2.5 bg-[#F5EFDC] border border-ink rounded-none text-sm text-text-primary"
            aria-label={t.needleSizeTo}
          >
            <option value="">{t.needleSizeTo}</option>
            {NEEDLE_SIZES.map(s => <option key={s} value={s}>{s} mm</option>)}
          </select>
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <label className="block text-xs font-medium text-text-secondary">{t.weight}</label>
          {!weightManual && suggestedWeight && (
            <Chip size="xs" tone="accent">{t.weightAutoTag}</Chip>
          )}
          {weightManual && suggestedWeight && suggestedWeight !== weight && (
            <button
              type="button"
              onClick={() => setWeightManual(false)}
              className="text-[10px] font-medium text-accent"
            >
              {t.weightAutoReset}
            </button>
          )}
        </div>
        <select
          value={weight}
          onChange={e => { setWeight(e.target.value); setWeightManual(true); }}
          className="w-full px-3.5 py-2.5 bg-[#F5EFDC] border border-ink rounded-none text-sm"
        >
          <option value="I don't know">{t.iDontKnow}</option>
          {['Lace', 'Fingering', 'Sport', 'DK', 'Worsted', 'Aran', 'Bulky', 'Super Bulky'].map(w => (
            <option key={w} value={w}>{w}</option>
          ))}
        </select>
        {!weightManual && suggestedWeight && (
          <ItalicBody style={{ marginTop: 4 }}>{t.weightAutoExplain}</ItalicBody>
        )}
      </div>

      <div>
        <MonoLabel>{t.mfgGauge}</MonoLabel>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <p className="text-[9px] text-[#3D3A33] mb-1 uppercase tracking-[0.14em] font-semibold">{t.stitches}</p>
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={mfgGaugeStitches || ''}
                onChange={e => setMfgGaugeStitches(parseInt(e.target.value) || 0)}
                placeholder="—"
                aria-label={`${t.stitches} ${t.needleSizeFrom}`}
                className="w-full px-2 py-2 bg-[#F5EFDC] border border-ink rounded-none text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span className="text-text-tertiary text-xs">–</span>
              <input
                type="number"
                value={mfgGaugeStitchesMax || ''}
                onChange={e => setMfgGaugeStitchesMax(parseInt(e.target.value) || 0)}
                placeholder="—"
                aria-label={`${t.stitches} ${t.needleSizeTo}`}
                className="w-full px-2 py-2 bg-[#F5EFDC] border border-ink rounded-none text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
          </div>
          <div>
            <p className="text-[9px] text-[#3D3A33] mb-1 uppercase tracking-[0.14em] font-semibold">{t.rows}</p>
            <input type="number" value={mfgGaugeRows || ''} onChange={e => setMfgGaugeRows(parseInt(e.target.value) || 0)} placeholder="—" className="w-full px-3 py-2 bg-[#F5EFDC] border border-ink rounded-none text-sm" />
          </div>
          <div>
            <p className="text-[9px] text-[#3D3A33] mb-1 uppercase tracking-[0.14em] font-semibold">{t.needleSize}</p>
            <input type="text" value={mfgGaugeNeedle} onChange={e => setMfgGaugeNeedle(e.target.value)} placeholder="e.g. 4mm" className="w-full px-3 py-2 bg-[#F5EFDC] border border-ink rounded-none text-sm" />
          </div>
        </div>
      </div>
      <div>
        <MonoLabel>{t.myGauge}</MonoLabel>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <p className="text-[9px] text-[#3D3A33] mb-1 uppercase tracking-[0.14em] font-semibold">{t.stitches}</p>
            <input type="number" value={myGaugeStitches || ''} onChange={e => setMyGaugeStitches(parseInt(e.target.value) || 0)} placeholder="—" className="w-full px-3 py-2 bg-[#F5EFDC] border border-ink rounded-none text-sm" />
          </div>
          <div>
            <p className="text-[9px] text-[#3D3A33] mb-1 uppercase tracking-[0.14em] font-semibold">{t.rows}</p>
            <input type="number" value={myGaugeRows || ''} onChange={e => setMyGaugeRows(parseInt(e.target.value) || 0)} placeholder="—" className="w-full px-3 py-2 bg-[#F5EFDC] border border-ink rounded-none text-sm" />
          </div>
          <div>
            <p className="text-[9px] text-[#3D3A33] mb-1 uppercase tracking-[0.14em] font-semibold">{t.needleSize}</p>
            <input type="text" value={myGaugeNeedle} onChange={e => setMyGaugeNeedle(e.target.value)} placeholder="e.g. 4.5mm" className="w-full px-3 py-2 bg-[#F5EFDC] border border-ink rounded-none text-sm" />
          </div>
        </div>
      </div>
      <Field label={t.shopLink} value={shopUrl} onChange={setShopUrl} placeholder="https://..." />

      <div>
        <MonoLabel>{t.review}</MonoLabel>
        <textarea value={review} onChange={e => setReview(e.target.value)} placeholder={t.reviewPlaceholder} rows={2} className="w-full px-3.5 py-2.5 bg-[#F5EFDC] border border-ink rounded-none text-sm resize-none" />
        <ItalicBody style={{ marginTop: 4 }}>{t.reviewSharedHint}</ItalicBody>
      </div>

      <div>
        <MonoLabel>{t.notes}</MonoLabel>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder={t.yarnNotesPlaceholder} rows={2} className="w-full px-3.5 py-2.5 bg-[#F5EFDC] border border-ink rounded-none text-sm resize-none" />
      </div>

      {/* B1 zine submit row — 2026-05-09 build 1186. Black "Add to
          stash" / "Save" button as the primary action; cancel sits
          left as a smaller bordered button. */}
      <div style={{ display: 'flex', gap: 8, paddingTop: 8 }}>
        <button
          type="button"
          onClick={onCancel}
          data-testid="yarn-edit-cancel"
          style={{
            width: 60,
            background: 'transparent',
            color: 'var(--ink)',
            border: '1px solid var(--ink)',
            fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
            fontWeight: 800,
            fontSize: 12,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            padding: '12px 0',
          }}
        >
          {t.cancel}
        </button>
        <button
          type="submit"
          data-testid="yarn-edit-save"
          style={{
            flex: 1,
            background: 'var(--ink)',
            color: 'var(--paper-1)',
            border: 'none',
            fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
            fontWeight: 800,
            fontSize: 14,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            padding: '12px 0',
          }}
        >
          {initial ? t.save : t.add} →
        </button>
      </div>
    </form>
  );
}

function Field({ label, value, onChange, placeholder, required }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; required?: boolean;
}) {
  // 2026-05-09 build 1186 — B1 zine treatment for the simple text
  // fields used inside YarnForm (name, fiber, colorName, etc.).
  // Mono uppercase label, paper2 box with 1px ink border, display
  // caps input value.
  return (
    <div>
      <label
        style={{
          display: 'block',
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          letterSpacing: '0.16em',
          color: 'var(--ink-2)',
          textTransform: 'uppercase',
          marginBottom: 5,
        }}
      >
        {label}
      </label>
      {/* 2026-05-10 build 1297 — Veronika: input text the user
          types should match the regular sans 14px weight 500
          recipe (same as the ¼ ½ ¾ chips), not the bold
          uppercase display-caps treatment we used for "frozen"
          tag values. Display caps stay for read-only display
          (yarn name in cards, brand chips, etc.); typed input
          is now plain sentence-case medium weight. */}
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        style={{
          width: '100%',
          padding: '10px 12px',
          background: 'var(--paper-2)',
          border: '1px solid var(--ink)',
          fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
          fontWeight: 500,
          fontSize: 14,
          color: 'var(--ink)',
          outline: 'none',
        }}
      />
    </div>
  );
}
